import { bySubstance, pageLabel, rankMarker, rankMarkerCss } from '../armKit';
import { cardPartCss, chipsRowCss, compactCardsCss } from '../surface';

import type { ArmSpec } from '../armRegistry';
import type { WorkFacts } from '../pageData';
import type { ProfileCard } from '../surface';

type Facts = Map<number, WorkFacts>;

const FLAG = 'corpusindex';

function armCss(scope: string): string {
  return (
    compactCardsCss(scope, {
      rowGap: '6px',
      itemGap: '0 10px',
      titleGrows: false,
    }) +
    cardPartCss('title', scope, 'line-height: 1.4;') +
    chipsRowCss(scope, { inline: true, nowrap: true, pill: false }) +
    rankMarkerCss(scope, {
      minWidth: '28px',
      fontSize: '20px',
      marginRight: '6px',
      narrowMinWidth: '22px',
    })
  );
}

function marked(card: ProfileCard): boolean {
  return card.flag(FLAG) != null;
}

function indexCards(cards: ProfileCard[], facts: Facts, from: number): void {
  cards.forEach((card, offset) => {
    card.setFlag(FLAG);
    card.collapse();
    card.prependToTitle(rankMarker(String(from + offset)));

    const pages = pageLabel(facts.get(card.workId)?.pageCount);
    if (pages != null) card.addChip(pages);
  });
}

export const corpusIndex: ArmSpec = {
  onReady: async (ctx) => {
    const facts = await ctx.workFacts();
    if (facts.size === 0 || ctx.cards().length === 0) return;

    ctx.injectStyles(armCss(ctx.scope));
    ctx.reorderCards(
      bySubstance((workId) => facts.get(workId)?.pageCount),
      'list',
    );

    // Read back rather than reuse the array above: the ranks must run with the
    // reordered DOM, which is the only record of it.
    indexCards(ctx.cards(), facts, 1);
  },

  // Numbered on from the last rank issued and never re-sorted: hoisting a late
  // batch past cards a reader is already scrolling costs more than a tail out
  // of order. Gated on the marks onReady left, so a batch cannot engage an arm
  // that degraded to control.
  onCards: async (cards, ctx) => {
    const facts = await ctx.workFacts();
    const issued = ctx.cards().filter(marked).length;
    if (issued === 0) return;

    indexCards(
      cards.filter((card) => !marked(card)),
      facts,
      issued + 1,
    );
  },
};
